import ReactJson from "react-json-view";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { fetchHrpTableItemsByHccID, fetchHrpTableItemsByPk, fetchHrpTableItemsBySk, fetchHrpTableItemsBySparse, fetchHrpTableScan, fetchItemsByRetryOn, fetchNotificationScheduleTableItemsByPk, fetchNotificationsTableItemsByConversationId, fetchNotificationsTableItemsByPk, fetchNotificationsTableScan } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import { queryClient } from "../../App";
import DatePicker from "react-datepicker";
import ToggleSwitch from "../shared/ToggleSwitch";
import { camelCaseToWords, getTimeDiff } from "../../utils/commonUtils";
import Select from 'react-select';

const searchOptions = [
    'recipientId',
    'conversationId',
].map(str => ({ value: str, label: camelCaseToWords(str) }))

const NotificationTable = ({ memberUuid, subscriptionHccId, autoRefreshEnabled: parentAutoRefreshEnabled }) => {

    const now = new Date()

    const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
    const localStartOfDayNow = new Date(now.getTime() - timezoneOffsetMs);
    localStartOfDayNow.setHours(0, 0, 0, 0);
    const [showMoreColumns, setShowMoreColumns] = useState(false);


    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();

    const [searchSkValue, setSearchSkValue] = useState();
    const [decouncedSearchSkValue, setDecouncedSearchSkValue] = useState();

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(parentAutoRefreshEnabled)

    const [selectedDate, setSelectedDate] = useState();

    useEffect(() => {
        setAutoRefreshEnabled(parentAutoRefreshEnabled)

    }, [parentAutoRefreshEnabled])

    useEffect(() => {
        if (subscriptionHccId) {
            setSelectedSearchOption({ value: 'hccId', label: 'hccId' })
            setSearchValue(subscriptionHccId)
            setDecouncedSearchValue(subscriptionHccId)
        }

    }, [subscriptionHccId])

    useEffect(() => {
        if (memberUuid) {
            setSelectedSearchOption({ value: 'recipientId', label: 'recipientId' })
            setSearchValue(memberUuid)
            setDecouncedSearchValue(memberUuid)
        }

    }, [memberUuid])




    const { ref, inView } = useInView();

    const queryKey = ['notiication-table', selectedDate, searchOptions?.value, decouncedSearchValue, decouncedSearchSkValue];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam };

            if (decouncedSearchValue && selectedSearchOption?.value) {
                // params[selectedSearchOption?.value] = decouncedSearchValue;


                if (selectedSearchOption?.value === 'conversationId') {
                    return fetchNotificationsTableItemsByConversationId({ ...params, conversationId: decouncedSearchValue })
                }


                if (selectedSearchOption?.value === 'recipientId') {
                    if (decouncedSearchSkValue) {
                        params.sk = decouncedSearchSkValue
                    }
                    return fetchNotificationsTableItemsByPk({ ...params, memberUuid: decouncedSearchValue })
                }



            }

            if (selectedDate) {
                return fetchItemsByRetryOn({ ...params, retryOn: selectedDate.toISOString()?.slice(0, 10) })
            }

            //is in local time
            // if (selectedDate) {
            //     selectedDate.setHours(0, 0, 0, 0);
            //     params.createdAt = selectedDate.toISOString()
            // }

            return fetchNotificationsTableScan(params)
        },
        select: (data) => {
            return {
                pages: data.pages.map((page) => page.Items?.map(item => {
                    if (item.requestBody) {
                        try {
                            item.requestBodyParsed = JSON.parse(item.requestBody)
                        } catch (err) {
                            console.log(err)
                        }
                    }

                    if (item.startedAt && item.completedAt) {
                        const date1 = new Date(item.startedAt);
                        const date2 = new Date(item.completedAt);
                        item.duration = getTimeDiff({ date1, date2 })
                    }
                    return item
                })).flatMap((x) => x),
                pageParams: data.pageParams,
            };
            // return { ...data.hits, timestamp: Date.now() }
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        staleTime: 0,//,Infinity,
        enabled: true  //&& !!showChat
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const debouncedSkChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchSkValue(event.target.value?.trim());
        }, 500),
        []
    );

    // const resutls = data?.pages.flat()
    const items = data?.pages.flat();

    let columns = ['recipientId', 'recipientType#notificationType#notificationEvent#notificationId', 'event', 'recipientFamilyId', 'recipientFirstName', 'recipientLastName', 'client', 'plan', 'scheduleId', 'conversationId', 'messageId', 'status', 'type', 'scheduledTimestamp', 'transactionDate', 'sendType', 'readDate', 'readTimestamp', 'created', 'modified', 'NotificationsTemplateName'];

    if (showMoreColumns) {
        columns = [
            ...columns,
            'event',

            'msgSubject',
            'msgText'

        ];
    }

    const today = new Date();
    // today.setDate(today.getDate() + 1);

    const onDetailHandler = async (payload) => {
        if (!payload.scheduleId) {
            return {}
        }
        const details = await fetchNotificationScheduleTableItemsByPk({ memberUuid: payload.recipientId, sk: payload.scheduleId })
        return { schedules: details.Items };
    }

    return (
        <div className="container"
            style={memberUuid ? { maxHeight: '100%', overflow: 'scroll' } : {}}
        >
            {!subscriptionHccId &&
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                        <p className="title">Notification Table</p>
                        <p className="sub-title">Notifications</p>
                        <p className="sub-title">Search hints pk: (receipientId | memberUuid) sk: recipientType#notificationType#notificationEvent#notificationId </p>

                    </div>
                    <div>
                        <ToggleSwitch
                            label={`Auto Refresh`}
                            onToggle={(val) => setAutoRefreshEnabled(val)}
                            initialState={autoRefreshEnabled}
                        />
                    </div>

                </div>

            }


            {!subscriptionHccId && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>

                    <Select
                        name={'searchOptions'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        options={searchOptions}
                        value={selectedSearchOption}
                        isDisabled={!!memberUuid}
                        onChange={(option) => { setSelectedSearchOption(option) }}
                    />


                    {selectedSearchOption?.value === 'retryOn' ? <DatePicker
                        className="date-picker"
                        placeholderText="Retry on"
                        inline={false}
                        dateFormat={'yyyy-MM-dd'}
                        selected={selectedDate}
                        onChange={(date) => {
                            setSelectedDate(date)
                        }}
                        minDate={today}
                    /> : selectedSearchOption?.value && <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder={'search'}
                        autoComplete="off"
                        disabled={!!memberUuid}
                        onChange={(e) => {

                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />}

                    {selectedSearchOption?.value === 'recipientId' && <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"sk"}
                        placeholder={'sk'}
                        autoComplete="off"
                        onChange={(e) => {

                            setSearchSkValue(e.target.value);
                            debouncedSkChangeHandler(e);
                        }}
                        value={searchSkValue}
                    />}

                </div>
            </div>}


            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", }}>
                {subscriptionHccId ? <p></p> : <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {

                        setSelectedSearchOption(null)
                        setSelectedDate(null);
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        setDecouncedSearchSkValue("");
                        setSearchSkValue("");
                    }}
                >
                    Clear filters
                </p>}

                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>


            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"hrp-table"}
                        columns={columns}
                        items={items}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        onDetailHandler={onDetailHandler}
                        ref={ref}
                    />
                </>
            )}


        </div>
    )
}

export default NotificationTable